import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { PLATFORMS, formatINR } from "../lib/brand";
import {
  RefreshCw,
  Package,
  Star,
  ExternalLink,
  Calendar,
  Sparkles,
  Link as LinkIcon,
} from "lucide-react";

export default function Catalogue() {
  const [data, setData] = useState({ items: [], total: 0, by_platform: {}, last_synced_at: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncUrl, setSyncUrl] = useState("");
  const [filter, setFilter] = useState("");

  const refresh = () => {
    setLoading(true);
    api
      .get("/seller/catalogue")
      .then((r) => setData(r.data || { items: [], total: 0, by_platform: {} }))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleSync = async (e) => {
    e.preventDefault();
    if (!syncUrl.trim()) return toast.error("Paste a brand URL to sync");
    setSyncing(true);
    try {
      const { data: res } = await api.post("/seller/catalogue/sync", { url: syncUrl.trim() });
      if (res.status === "ok") {
        toast.success(
          `Synced ${res.synced_count} products from ${res.platform} · ${res.new_count} new`
        );
        setSyncUrl("");
        refresh();
      } else {
        toast.error(res.error_hint || res.error || "Sync failed");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSyncing(false);
    }
  };

  const filtered = data.items.filter((it) => {
    if (!filter) return true;
    return filter === it.platform;
  });

  return (
    <AppShell role="seller">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#4F46E5] font-bold">
            My Catalogue
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-2">
            Your live SKU master.
          </h1>
          <p className="text-white/60 text-sm mt-1 max-w-xl">
            Every product we've fetched from your brand pages, cached here for instant reuse.
            Pull updates anytime — we'll detect new listings & delisted SKUs.
          </p>
        </div>
        {data.last_synced_at && (
          <div className="text-[11px] text-white/50 uppercase tracking-widest flex items-center gap-1.5">
            <Calendar size={12} />
            Last synced {new Date(data.last_synced_at).toLocaleString("en-IN")}
          </div>
        )}
      </div>

      {/* Sync form */}
      <form
        onSubmit={handleSync}
        className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-5 text-white mb-6 flex flex-col md:flex-row md:items-center gap-3"
        data-testid="catalogue-sync-form"
      >
        <div className="flex items-center gap-2 flex-1 border border-white/[0.08] rounded-md px-3 py-2 focus-within:border-[#4F46E5]">
          <LinkIcon size={14} className="text-slate-500 shrink-0" />
          <input
            type="url"
            value={syncUrl}
            onChange={(e) => setSyncUrl(e.target.value)}
            placeholder="https://www.myntra.com/<your-brand>"
            className="flex-1 text-sm focus:outline-none bg-transparent"
            data-testid="catalogue-sync-url"
          />
        </div>
        <button
          type="submit"
          disabled={syncing}
          className="btn-primary text-sm disabled:opacity-60"
          data-testid="catalogue-sync-btn"
        >
          <RefreshCw
            size={14}
            className={`inline mr-1.5 ${syncing ? "animate-spin" : ""}`}
          />
          {syncing ? "Pulling catalogue…" : "Pull my catalogue"}
        </button>
      </form>

      {/* Platform filter tabs */}
      {Object.keys(data.by_platform).length > 0 && (
        <div className="flex items-center gap-2 mb-5" data-testid="catalogue-tabs">
          <button
            onClick={() => setFilter("")}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border-2 transition-all ${
              filter === ""
                ? "border-[#4F46E5] bg-[#4F46E5] text-white"
                : "border-white/15 text-white/70 hover:border-white/40"
            }`}
          >
            All · {data.total}
          </button>
          {Object.entries(data.by_platform).map(([pid, count]) => {
            const meta = PLATFORMS.find((p) => p.id === pid);
            const on = filter === pid;
            return (
              <button
                key={pid}
                onClick={() => setFilter(pid)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider border-2 transition-all ${
                  on
                    ? "border-[#4F46E5] bg-[#4F46E5] text-white"
                    : "border-white/15 text-white/70 hover:border-white/40"
                }`}
                style={on ? {} : { borderColor: `${meta?.color}55` }}
              >
                {pid} · {count}
              </button>
            );
          })}
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="aspect-square rounded-lg bg-white/[0.025] backdrop-blur-xl border border-white/[0.08]/5 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div
          className="bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-xl p-10 text-white"
          data-testid="catalogue-empty"
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={18} className="text-[#4F46E5]" />
            <div className="font-display text-2xl font-black tracking-tighter">
              Your catalogue is empty.
            </div>
          </div>
          <p className="text-slate-400 text-sm">
            Paste your brand store URL above and hit "Pull my catalogue" to cache
            every product. Next time you upload a CSV, we'll auto-match without
            another scrape.
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3"
          data-testid="catalogue-grid"
        >
          {filtered.map((it) => (
            <a
              key={`${it.platform}-${it.product_id}`}
              href={it.url}
              target="_blank"
              rel="noreferrer"
              className="group bg-white/[0.025] backdrop-blur-xl border border-white/[0.08] rounded-lg overflow-hidden text-white hover:ring-2 hover:ring-[#4F46E5]/60 transition-all"
              data-testid={`catalogue-item-${it.product_id}`}
            >
              <div className="aspect-square overflow-hidden bg-white/[0.04]">
                {it.image_url ? (
                  <img
                    src={it.image_url}
                    alt={it.title}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://images.unsplash.com/photo-1607082349566-187342175e2f?w=400&q=75";
                    }}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-300">
                    <Package size={28} />
                  </div>
                )}
              </div>
              <div className="p-2">
                <div className="text-[9px] uppercase tracking-widest font-bold text-slate-500">
                  {it.brand || it.platform}
                </div>
                <div className="text-[11px] font-bold leading-tight line-clamp-2 min-h-[2rem] mt-0.5">
                  {it.title}
                </div>
                <div className="mt-1.5 flex items-center justify-between">
                  <span className="font-mono-data text-xs font-black">
                    {formatINR(it.sale_price || it.mrp)}
                  </span>
                  {it.rating > 0 && (
                    <span className="text-[10px] text-amber-600 inline-flex items-center gap-0.5">
                      <Star size={9} fill="currentColor" /> {it.rating}
                    </span>
                  )}
                </div>
                {!it.in_stock && (
                  <div className="text-[9px] text-[#4F46E5] font-bold uppercase tracking-wider mt-0.5">
                    Out of stock
                  </div>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </AppShell>
  );
}
